// src/components/atomic-crm/activities/ActivityStream.tsx
// src/components/atomic-crm/activities/ActivityStream.tsx
import { useMemo, useState } from "react";
import { useGetIdentity, useTranslate, useLocale } from "ra-core";
import { Card, CardContent } from "@/components/ds/ui/card";
import { Badge } from "@/components/ds/ui/badge";
import { Skeleton } from "@/components/ds/ui/skeleton";
import {
  CheckCircle2,
  Circle,
  Clock,
  CalendarClock,
  Radio,
  StickyNote,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { getDateFnsLocale } from "@/i18n/date-fns";
import { AddTask } from "../tasks/AddTask";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { ActivityComposer } from "./ActivityComposer";

type Resource = "companies" | "contacts" | "deals";
type ActivityFilter = "all" | "notes" | "tasks" | "mine"| "system" ;

interface ActivityStreamProps {
  resource: Resource;
  recordId: number;
  record?: any;
  contactIds?: number[];
}

const FILTERS: { key: ActivityFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "notes", label: "Notes" },
  { key: "tasks", label: "Tasks" },
  { key: "mine", label: "Mine" },
  { key: "system", label: "System" }, // 👈 new
];

export const ActivityStream = ({
                                 resource,
                                 recordId,
                                 record,
                                 contactIds,
                               }: ActivityStreamProps) => {
  const translate = useTranslate();
  const [filter, setFilter] = useState<ActivityFilter>("all");

  const { identity } = useGetIdentity();
  const mySalesId = identity?.id != null ? Number(identity.id) : undefined;

  const { data: activities = [], isLoading } = useQuery({
    queryKey: [
      "crm_activities",
      resource,
      recordId,
      contactIds?.join(",") ?? "",
    ],
    queryFn: async () => {
      let q = supabase.from("crm_activities").select("*");

      if (resource === "deals") {
        q = contactIds?.length
          ? q.or(
            `deal_id.eq.${recordId},contact_id.in.(${contactIds.join(",")})`,
          )
          : q.eq("deal_id", recordId);
      } else if (resource === "contacts") {
        q = q.eq("contact_id", recordId);
      } else if (resource === "companies") {
        q = contactIds?.length
          ? q.or(
            `company_id.eq.${recordId},contact_id.in.(${contactIds.join(",")})`,
          )
          : q.eq("company_id", recordId);
      }

      const { data, error } = await q
        .order("date", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  const activityIds = useMemo(
    () => activities.map((a: any) => a.id),
    [activities],
  );

  const { data: myAssignmentRows = [] } = useQuery({
    queryKey: ["crm_activity_assignees", "mine", mySalesId, activityIds],
    queryFn: async () => {
      if (!mySalesId || activityIds.length === 0) return [];
      const { data, error } = await supabase
        .from("crm_activity_assignees")
        .select("activity_id")
        .eq("sales_id", mySalesId)
        .in("activity_id", activityIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!mySalesId && activityIds.length > 0 && filter === "mine",
  });

  const myAssignedActivityIds = useMemo(
    () => new Set(myAssignmentRows.map((r: any) => Number(r.activity_id))),
    [myAssignmentRows],
  );

  const { topLevel, tasksByParent } = useMemo(() => {
    const children = new Map<number, any[]>();
    const top: any[] = [];
    activities.forEach((a: any) => {
      if (a.type === "task" && a.parent_activity_id) {
        const list = children.get(a.parent_activity_id) ?? [];
        list.push(a);
        children.set(a.parent_activity_id, list);
      } else {
        top.push(a);
      }
    });
    return { topLevel: top, tasksByParent: children };
  }, [activities]);

  const visible = useMemo(() => {
    if (filter === "all") return topLevel;
    if (filter === "notes") return topLevel.filter((a) => a.type === "note");
    if (filter === "tasks") return topLevel.filter((a) => a.type === "task");
    if (filter === "mine") {
      return topLevel.filter((a) => {
        if (a.sales_id === mySalesId) return true;
        if (myAssignedActivityIds.has(Number(a.id))) return true;
        return false;
      });
    }
    if (filter === "system") {
      return topLevel.filter(
        (a) => a.source === "system" || a.source === "ai",
      );
    }
    return topLevel;
  }, [topLevel, filter, mySalesId, myAssignedActivityIds]);


  const emptyMessage = (() => {
    switch (filter) {
      case "notes":
        return translate("crm.activity.empty.notes", { _: "No notes yet." });
      case "tasks":
        return translate("crm.activity.empty.tasks", { _: "No tasks yet." });
      case "mine":
        return translate("crm.activity.empty.mine", {
          _: "Nothing assigned to you here.",
        });
      case "system":
        return translate("crm.activity.empty.system", {
          _: "No system events yet.",
        });
      default:
        return translate("crm.activity.empty", { _: "No activity yet." });
    }
  })();

  if (isLoading) {
    return (
      <div className="space-y-4 pt-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-2">
      {/* Unified composer — replaces the old NoteCreate */}
      {record && (
        <ActivityComposer
          resource={resource}
          recordId={recordId}
          record={record}
        />
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                "px-3 py-1 text-xs rounded-full border transition-colors",
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground",
              )}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {visible.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
          {emptyMessage}
        </div>
      ) : (
        visible.map((activity) =>
          activity.type === "note" ? (
            activity.source === "system" || activity.source === "ai" ? (
              <SystemEventCard key={activity.id} event={activity} />
            ) : (
              <NoteCard
                key={activity.id}
                note={activity}
                childTasks={tasksByParent.get(activity.id) ?? []}
                resource={resource}
              />
            )
          ) : (
            <StandaloneTaskCard key={activity.id} task={activity} />
          ),
        )
      )}
    </div>
  );
};

// NoteCard, StandaloneTaskCard, TaskRow — unchanged from your current file

// ---------------------------------------------------------------------------
// Note card
// ---------------------------------------------------------------------------

const NoteCard = ({
  note,
  childTasks,
  resource,
}: {
  note: any;
  childTasks: any[];
  resource: Resource;
}) => {
  const locale = useLocale();

  return (
    <Card className="border-l-4 border-l-info/40">
      <CardContent className="pt-4">
        <div className="flex items-start gap-3">
          <StickyNote className="h-5 w-5 text-info mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground flex items-center gap-2 mb-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(note.date), {
                addSuffix: true,
                locale: getDateFnsLocale(locale),
              })}
              {note.status && (
                <Badge variant="outline" className="text-[10px] h-4">
                  {note.status}
                </Badge>
              )}
            </div>
            <div className="text-sm whitespace-pre-wrap">{note.text}</div>
          </div>
        </div>

        {childTasks.length > 0 && (
          <div className="mt-3 ml-8 space-y-2 border-l-2 border-muted pl-4">
            {childTasks.map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
          </div>
        )}

        <div className="mt-3 ml-8">
          <AddTask
            display="button"
            selectContact={resource === "contacts"}
            noteId={note.id}
            noteType={
              resource === "companies"
                ? "company"
                : resource === "contacts"
                  ? "contact"
                  : "deal"
            }
            parentId={
              resource === "deals"
                ? note.deal_id
                : resource === "contacts"
                  ? note.contact_id
                  : note.company_id
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Task cards
// ---------------------------------------------------------------------------

const StandaloneTaskCard = ({ task }: { task: any }) => (
  <Card className={task.done_date ? "opacity-70" : ""}>
    <CardContent className="pt-4">
      <TaskRow task={task} />
    </CardContent>
  </Card>
);

const TaskRow = ({ task }: { task: any }) => {
  const locale = useLocale();
  const isDone = !!task.done_date;
  const isOverdue =
    !isDone && task.due_date && new Date(task.due_date) < new Date();
  const d = new Date(task.due_date);
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;


  return (
    <div className="flex items-start gap-2 text-sm">
      {isDone ? (
        <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" />
      ) : (
        <Circle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className={isDone ? "line-through text-muted-foreground" : ""}>
          {task.text || "(untitled task)"}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
          {task.due_date && !isDone && (
            <>
              <CalendarClock className="h-3 w-3" />

             <span className={isOverdue ? "text-destructive font-medium" : ""}>
                  {hasTime
                    ? format(d, "PPp", { locale: getDateFnsLocale(locale) })
                    : format(d, "PP", { locale: getDateFnsLocale(locale) })}
                </span>
            </>
          )}
          {isDone && task.done_date && (
            <>
              <CheckCircle2 className="h-3 w-3" />
              Done{" "}
              {formatDistanceToNow(new Date(task.done_date), {
                addSuffix: true,
                locale: getDateFnsLocale(locale),
              })}
            </>
          )}
          {task.priority && (
            <Badge
              variant={
                task.priority?.toLowerCase() === "high"
                  ? "destructive"
                  : task.priority?.toLowerCase() === "medium"
                    ? "default"
                    : "secondary"
              }
              className="text-[10px] h-4"
            >
              {task.priority}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};
const SystemEventCard = ({ event }: { event: any }) => {
  const locale = useLocale();
  return (
    <Card className="border border-dashed bg-muted/20">
      <CardContent className="pt-3 pb-3">
        <div className="flex items-start gap-3">
          <Radio className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground flex items-center gap-2 mb-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(event.date), {
                addSuffix: true,
                locale: getDateFnsLocale(locale),
              })}
              <Badge variant="outline" className="text-[10px] h-4">
                {event.source}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">{event.text}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
